/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * File step of Labels Import wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.0
 */

import { useCallback, useMemo } from 'react';

import isEqual from 'lodash/isEqual';
import { useDropzone } from 'react-dropzone';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Button } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { EFileExtension, MapExtensionToIcon } from '../../../modules/app/appConstants';
import { makeSelectLabelsImportTranslation } from '../../../modules/internationalization/intSelectors';
import { ILabelsImportTranslation } from '../../../modules/internationalization/types/wizards/labelsImport';
import { IRootReducer } from '../../../reducers/rootReducer';
import { getFileExtension } from '../../../utils/formatText';
import { stringFormatParams } from '../../../utils/stringFormat';
import {
    labelsImportSetFileError,
    labelsImportSetFileObjects,
    labelsImportSetFileUploaded,
} from '../labelsImportActions';
import { ELabelsImportValidationCodes } from '../labelsImportConstants';
import { ILabelsImportFileInfoItem, ILabelsImportFileMeta } from '../labelsImportReducer';
import {
    makeSelectLabelsImportFileError,
    makeSelectLabelsImportFileInfoItems,
    makeSelectLabelsImportFileMeta,
    makeSelectLabelsImportFileObjects,
} from '../labelsImportSelectors';
import { ILabelsImportSetFileUploadedPayload } from '../labelsImportTypes';
import {
    LabelsImportAttachedFileCloseStyled,
    LabelsImportAttachedFileContainerStyled,
    LabelsImportAttachedFileNameStyled,
    LabelsImportFileContainerStyled,
    LabelsImportFileErrorStyled,
    LabelsImportFileHeaderStyled,
    LabelsImportFileMessagesContainerStyled,
    LabelsImportStepFileDropzoneStyled,
} from './styled/LabelsImportStepFileStyled';

export type TLabelsImportStepFileProps = IConnectedProps & IDispatchProps;

export const LabelsImportStepFile = (props: TLabelsImportStepFileProps) => {
    const onDropHandler = useCallback((acceptedFiles: File[]) => {
        const error = handleFileValidate(acceptedFiles[0]);
        if (error) {
            props.setFileError(error);
        } else {
            props.setFileList(acceptedFiles);
        }
    }, [props.meta])

    const convertBytesToMb = useCallback((mb: number): number => {
        return mb / 1024 / 1024;
    }, []);

    const handleFileValidate = (file: File): ELabelsImportValidationCodes | null => {
        const fileExtension = getFileExtension(file.name);
        if (!fileExtension || !props.meta.templateFormats.includes(fileExtension)) {
            return ELabelsImportValidationCodes.FILE_FORMAT_INCORRECT;
        } else if (file.size >= props.meta.maxFileSize) {
            return ELabelsImportValidationCodes.FILE_SIZE_EXCEEDED;
        }

        return null;
    }

    const unsetFile = useCallback((file: File) => {
        props.setFileList(props.fileList.filter(item => !isEqual(item, file)));
    }, [props.fileList])

    const handleUnsetFile = useCallback((name: string) => {
        const fileToUnset: File | undefined = props.fileList.find(file => file.name === name);
        if (fileToUnset) {
            unsetFile(fileToUnset);
        } else {
            props.setFileList([]);
        }
        props.setFileUploaded({
            isFileUploaded: false,
            isMaxRowsExceeded: false,
            totalRowsCount: 0,
        })
    }, [props.fileList])

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: false,
        noClick: false,
        noKeyboard: false,
    });

    const templateFormatsMessage =
        useMemo(() => props.meta.templateFormats.map(format => format.toUpperCase()).join(', '),
            [props.meta]);

    const errorMessage = props.labelsImportTranslation.error.codes[props.fileError];

    const formFileRequirementMessage = () => {
        const maxFileSizeMb = convertBytesToMb(props.meta.maxFileSize).toFixed(2);
        return stringFormatParams(props.labelsImportTranslation.fileRequirementsMessage,
            { ...props.meta, maxFileSize: maxFileSizeMb, templateFormats: templateFormatsMessage });
    }

    return (
        <LabelsImportFileContainerStyled>

            <LabelsImportFileHeaderStyled>
                {props.labelsImportTranslation.chooseFileHeader}
            </LabelsImportFileHeaderStyled>

            <LabelsImportStepFileDropzoneStyled {...getRootProps()} isActive={isDragActive}>
                <ImgStyled className="medium" src={icons.defaultFileIcon}/>
                <span dangerouslySetInnerHTML={{ __html: props.labelsImportTranslation.chooseFileDropZoneText }}/>
            </LabelsImportStepFileDropzoneStyled>

            {/* Hidden input */}
            <input {...getInputProps()} />
            <Button onClick={open}>
                {props.labelsImportTranslation.chooseFileButton}
            </Button>

            {
                !!props.fileInfoItems.length && !props.fileError &&
                props.fileInfoItems.map(item => (
                    <LabelsImportAttachedFileContainerStyled key={item.name}>
                        <ImgStyled
                            className="medium"
                            src={MapExtensionToIcon[getFileExtension(item.name) as EFileExtension] ??
                            icons.defaultFileIcon}/>
                        <LabelsImportAttachedFileNameStyled>
                            {item.name}
                        </LabelsImportAttachedFileNameStyled>
                        <LabelsImportAttachedFileCloseStyled onClick={() => handleUnsetFile(item.name)}/>
                    </LabelsImportAttachedFileContainerStyled>
                ))
            }

            {
                !!props.fileError &&
                <LabelsImportFileErrorStyled>
                    {errorMessage ? stringFormatParams(errorMessage, { templateFormatsMessage }) :
                        props.labelsImportTranslation.error.defaultMessage}
                </LabelsImportFileErrorStyled>
            }

            <LabelsImportFileMessagesContainerStyled>
                <div dangerouslySetInnerHTML={{ __html: formFileRequirementMessage() }}/>
            </LabelsImportFileMessagesContainerStyled>
        </LabelsImportFileContainerStyled>
    )
};

interface IConnectedProps {
    fileError: string;
    fileInfoItems: ILabelsImportFileInfoItem[];
    fileList: File[];
    meta: ILabelsImportFileMeta;
    labelsImportTranslation: ILabelsImportTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    fileError: makeSelectLabelsImportFileError(),
    fileInfoItems: makeSelectLabelsImportFileInfoItems(),
    fileList: makeSelectLabelsImportFileObjects(),
    meta: makeSelectLabelsImportFileMeta(),
    labelsImportTranslation: makeSelectLabelsImportTranslation(),
});

interface IDispatchProps {
    setFileError: (payload: string) => void;
    setFileList: (payload: File[]) => void;
    setFileUploaded: (payload: ILabelsImportSetFileUploadedPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setFileError: payload => dispatch(labelsImportSetFileError({ error: payload })),
    setFileList: payload => dispatch(labelsImportSetFileObjects({ fileObjects: payload })),
    setFileUploaded: payload => dispatch(labelsImportSetFileUploaded(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelsImportStepFile);
